import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  memo,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Form,
  Button,
  Card,
  Badge,
  Alert,
  Modal,
  Container,
  Row,
  Col,
  ProgressBar,
  Nav,
  Tab,
  Spinner,
} from "react-bootstrap";
import {
  IconUser,
  IconPalette,
  IconBuilding,
  IconSettings,
  IconFunction,
  IconFileText,
  IconChevronLeft,
  IconChevronRight,
  IconEye,
  IconDeviceFloppy,
  IconPhoto,
  IconHeart,
  IconWand,
  IconSparkles,
  IconMessageCircle,
  IconRocket,
} from "@tabler/icons-react";
import Select from "react-select";
import Swal from "sweetalert2";

// React DND imports
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Importar services
import {
  // Agent services
  createAgent,
  updateAgent,
  copyAgentPrompt,

  // Image services
  listImages,
  uploadImage,
  bulkUploadImages,
  deleteImage,
  createImageFormData,
  createBulkImageFormData,

  // Draft services
  getCurrentDraft,
  saveDraft as saveDraftAPI,
  clearDraft,
  prepareDraftData,
  prepareAgentData,
} from "../../../services/AgentsServices";
import UserContext from "../../../contexts/UserContext";
import StepIdentity from "./components/step1";
import {
  AREA_ACTIVITY_OPTIONS,
  CONVERSATION_TEMPLATES,
  MICRO_INTERACTIONS,
  NEXT_STEP_OPIONS,
} from "./AgentsConstants";
import StepLanguage from "./components/step2";
import StepBusiness from "./components/step3";
import StepConversation from "./components/step4";
import StepFunctions from "./components/step5";
import StepInteractions from "./components/step6";
import StepFinalPrompt from "./components/step7";
import ImageLibraryTab from "./components/imageLibrary";
import NotificationsContext from "../../../contexts/NotificationsContext";

// Componente DnD para etapas
export const DraggableStepItem = ({ step, index, moveStep, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "STEP_ITEM",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "STEP_ITEM",
    hover: (item) => {
      if (item.index !== index) {
        moveStep(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
      }}
    >
      {children}
    </div>
  );
};

const AIFillModal = ({ show, onHide, onGenerate, stepInfo, loading }) => {
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (show && stepInfo) {
      const defaultPrompt = `Crie um texto para a etapa "${
        stepInfo.label
      }" de um agente de IA para ${stepInfo.businessArea || "um negócio"}. 

Características do agente:
- Nome: ${stepInfo.agentName || "Não informado"}
- Área: ${stepInfo.businessArea || "Não informado"}
- Personalidade: ${stepInfo.personality || "Não informado"}

Contexto da etapa: ${stepInfo.description || ""}

O texto deve ser:
- Natural e humanizado
- Direto ao ponto
- Apropriado para WhatsApp
- Sem formatação markdown

Retorne apenas o texto que o agente deve falar, sem explicações adicionais.`;

      setPrompt(defaultPrompt);
    }
  }, [show, stepInfo]);

  const handleGenerate = () => {
    onGenerate(prompt);
    setPrompt("");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconWand className="me-2" size={20} />
          Preencher com IA
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <IconSparkles className="me-2" size={16} />A IA irá gerar o texto para
          a etapa "<strong>{stepInfo?.label}</strong>" baseado nas informações
          do seu agente.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Prompt para a IA</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Descreva o que você quer que a IA gere para esta etapa..."
          />
          <Form.Text className="text-muted">
            Você pode editar este prompt para obter resultados mais específicos
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Gerando...
            </>
          ) : (
            <>
              <IconWand className="me-2" size={16} />
              Gerar com IA
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const schemas = {
  step1: yup.object().shape({
    agentName: yup.string().required("Nome do agente é obrigatório"),
    agentJob: yup.string().required("Função do agente é obrigatório"),
    agentGender: yup.string().required("Gênero é obrigatório"),
    agentPersona: yup.array().min(1, "Selecione pelo menos uma personalidade"),
    agentArea: yup.string().required("Área de atuação é obrigatória"),
  }),
  step2: yup.object().shape({
    language: yup.string(),
    languageStyle: yup.array(),
    fundamentalRules: yup.array(),
    prohibitedWords: yup.string(),
    preferredWords: yup.string(),
  }),
  step3: yup.object().shape({
    businessInfo: yup
      .string()
      .required("Informações sobre o negócio são obrigatórias")
      .max(1200, "Máximo de 1200 caracteres"),
  }),
  step4: yup.object().shape({
    defaultFlowSteps: yup
      .array()
      .min(1, "Deve haver pelo menos uma etapa no fluxo padrão")
      .of(
        yup.object().shape({
          id: yup.string().required("ID da etapa é obrigatório"),
          label: yup.string().required("Nome da etapa é obrigatório"),
          key: yup.string().required("Chave da etapa é obrigatória"),
        })
      ),
    defaultFlow: yup.object(),
    specialFlows: yup.array(),
  }),
  step5: yup.object().shape({
    functionsEnabled: yup.boolean(),
  }),
  step6: yup.object().shape({
    interactions: yup.object(),
  }),
};

const AgentWizard = memo(({ imageLibrary = [] }) => {
  const { id } = useParams();
  const location = useLocation();
  const { data, isEditing } = location.state;

  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [currentStep, setCurrentStep] = useState(1);
  // const [showPreview, setShowPreview] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  const [activeTab, setActiveTab] = useState("wizard");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [apiImageLibrary, setApiImageLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const [getGeneratedSchemas, setGetGeneratedSchemas] = useState(null);

  // Novos estados para melhorias
  const [flowMode, setFlowMode] = useState("basic"); // "basic" ou "advanced"
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiStepInfo, setAiStepInfo] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // const [previewTrigger, setPreviewTrigger] = useState(0);

  // Form setup
  const form = useForm({
    defaultValues: {
      agentName: "",
      agentJob: "",
      agentGender: "",
      agentPersona: [],
      agentArea: "",
      languageStyle: [],
      fundamentalRules: ["noMarkdown", "avoidFormal"],
      prohibitedWords: "",
      preferredWords: "",
      businessInfo: "",
      address: "",
      paymentMethods: [],

      // ✅ ADICIONAR: Etapas dinâmicas do fluxo padrão
      defaultFlowSteps: [
        {
          id: "step_" + Date.now(),
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          placeholder:
            "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
          order: 0,
        },
      ],

      defaultFlow: {
        greeting: "",
        presentation: "",
        needIdentification: "",
        direction: "",
        confirmation: "",
        upsell: "",
        availability: "",
        dataCollection: "",
        closing: "",
        greetingNextStep: "",
        presentationNextStep: "",
        needIdentificationNextStep: "",
        directionNextStep: "",
        confirmationNextStep: "",
        upsellNextStep: "",
        availabilityNextStep: "",
        dataCollectionNextStep: "",
        closingNextStep: "",
        customStepNames: {},
      },
      specialFlows: [],
      functionsEnabled: false,
      functionSchemas: {},
      interactions: {
        conectores: {
          enabled: false,
          value: MICRO_INTERACTIONS.conectores.defaultValue,
        },
        validacao: {
          enabled: false,
          value: MICRO_INTERACTIONS.validacao.defaultValue,
        },
        celebracao: {
          enabled: false,
          value: MICRO_INTERACTIONS.celebracao.defaultValue,
        },
        upsell: {
          enabled: false,
          value: MICRO_INTERACTIONS.upsell.defaultValue,
        },
        emojis: {
          enabled: false,
          value: MICRO_INTERACTIONS.emojis.defaultValue,
        },
        mantra: {
          enabled: false,
          value: MICRO_INTERACTIONS.mantra.defaultValue,
        },
      },
    },
    resolver:
      currentStep <= 7
        ? yupResolver(schemas[`step${currentStep}`] || yup.object())
        : undefined,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
    reset,
  } = form;

  const specialFlowsRef = useRef([]);
  const prevSpecialFlowsRef = useRef("");
  const specialFlows = watch("specialFlows") || [];

  // Função para mapear dados da API para o formato do formulário
  const mapApiDataToForm = useCallback((apiData) => {
    if (!apiData) return {};

    console.log("🔍 DEBUG - Dados originais da API:", apiData);

    const formData = {};

    // ===================================================================
    // CAMPOS BÁSICOS DO AGENTE
    // ===================================================================

    // Nome do agente
    if (apiData.name || apiData.agent_name) {
      formData.agentName = apiData.name || apiData.agent_name;
    }

    //Função do agente
    if (apiData.agent_job) {
      formData.agentJob = apiData.agent_job;
    }

    // Gênero do agente
    if (apiData.agent_gender || apiData.gender) {
      formData.agentGender = apiData.agent_gender || apiData.gender;
    }

    // Área de atuação
    if (apiData.agent_area || apiData.area) {
      formData.agentArea = apiData.agent_area || apiData.area;
    }

    // ===================================================================
    // CAMPOS DE ARRAY
    // ===================================================================

    // Personalidade do agente
    if (apiData.agent_persona || apiData.persona) {
      formData.agentPersona = Array.isArray(apiData.agent_persona)
        ? apiData.agent_persona
        : Array.isArray(apiData.persona)
        ? apiData.persona
        : [];
    }

    if (apiData.language) {
      formData.language = apiData.language;
    }

    // Estilo de linguagem
    if (apiData.language_style || apiData.languageStyle) {
      formData.languageStyle = Array.isArray(apiData.language_style)
        ? apiData.language_style
        : Array.isArray(apiData.languageStyle)
        ? apiData.languageStyle
        : [];
    }

    // Regras fundamentais
    if (apiData.fundamental_rules || apiData.fundamentalRules) {
      formData.fundamentalRules = Array.isArray(apiData.fundamental_rules)
        ? apiData.fundamental_rules
        : Array.isArray(apiData.fundamentalRules)
        ? apiData.fundamentalRules
        : ["noMarkdown", "avoidFormal"];
    }

    // Métodos de pagamento
    if (apiData.payment_methods || apiData.paymentMethods) {
      formData.paymentMethods = Array.isArray(apiData.payment_methods)
        ? apiData.payment_methods
        : Array.isArray(apiData.paymentMethods)
        ? apiData.paymentMethods
        : [];
    }

    // ===================================================================
    // CAMPOS DE TEXTO
    // ===================================================================

    // Palavras proibidas
    if (apiData.prohibited_words || apiData.prohibitedWords) {
      formData.prohibitedWords =
        apiData.prohibited_words || apiData.prohibitedWords || "";
    }

    // Palavras preferidas
    if (apiData.preferred_words || apiData.preferredWords) {
      formData.preferredWords =
        apiData.preferred_words || apiData.preferredWords || "";
    }

    // Informações do negócio
    if (apiData.business_info || apiData.businessInfo) {
      formData.businessInfo =
        apiData.business_info || apiData.businessInfo || "";
    }

    // Endereço
    if (apiData.address) {
      formData.address = apiData.address || "";
    }

    // ===================================================================
    // FLUXO PADRÃO (DEFAULT FLOW) - ✅ CORRIGIDO para estrutura flat
    // ===================================================================

    let defaultFlowData = null;

    // ✅ CORREÇÃO: Verificar múltiplas estruturas possíveis
    if (
      apiData.default_flow ||
      apiData.default_flow_steps ||
      apiData.defaultFlowSteps
    ) {
      // Estrutura aninhada (formato antigo)
      defaultFlowData =
        apiData.default_flow ||
        apiData.default_flow_steps ||
        apiData.defaultFlowSteps;
      console.log(
        "🔍 DEBUG - Fluxo padrão encontrado em estrutura aninhada:",
        defaultFlowData
      );
    } else if (apiData.steps && Array.isArray(apiData.steps)) {
      // ✅ NOVO: Estrutura flat no nível raiz (formato atual)
      defaultFlowData = { ...apiData }; // Copiar tudo da raiz
      console.log(
        "🔍 DEBUG - Fluxo padrão encontrado em estrutura flat (raiz):",
        defaultFlowData
      );
    }

    if (defaultFlowData) {
      if (Array.isArray(defaultFlowData)) {
        // Se vier como array, usar como steps
        formData.defaultFlowSteps = defaultFlowData;
        formData.defaultFlow = {
          greeting: "",
          presentation: "",
          needIdentification: "",
          direction: "",
          confirmation: "",
          upsell: "",
          availability: "",
          dataCollection: "",
          closing: "",
          greetingNextStep: "",
          presentationNextStep: "",
          needIdentificationNextStep: "",
          directionNextStep: "",
          confirmationNextStep: "",
          upsellNextStep: "",
          availabilityNextStep: "",
          dataCollectionNextStep: "",
          closingNextStep: "",
          customStepNames: {},
        };
      } else {
        // ✅ CORREÇÃO: Se vier como objeto, extrair steps e dados corretamente

        // Extrair steps (metadados das etapas)
        formData.defaultFlowSteps = defaultFlowData.steps || [
          {
            id: "step_" + Date.now(),
            key: "greeting",
            label: "Saudação",
            icon: "👋",
            description:
              "Como o agente vai cumprimentar cada pessoa que chegar",
            placeholder:
              "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
            order: 0,
          },
        ];

        // ✅ CORREÇÃO: Filtrar e copiar apenas campos relevantes para defaultFlow
        const relevantFields = {};

        // Copiar campos de conteúdo das etapas (baseado nas chaves dos steps)
        if (defaultFlowData.steps && Array.isArray(defaultFlowData.steps)) {
          defaultFlowData.steps.forEach((step) => {
            if (step.key) {
              // Copiar conteúdo da etapa
              if (defaultFlowData[step.key] !== undefined) {
                relevantFields[step.key] = defaultFlowData[step.key];
              }

              // Copiar próxima etapa
              const nextStepKey = `${step.key}NextStep`;
              if (defaultFlowData[nextStepKey] !== undefined) {
                relevantFields[nextStepKey] = defaultFlowData[nextStepKey];
              }
            }
          });
        }

        // Copiar campos padrão conhecidos
        const knownDefaultFlowFields = [
          "greeting",
          "presentation",
          "needIdentification",
          "direction",
          "confirmation",
          "upsell",
          "availability",
          "dataCollection",
          "closing",
          "greetingNextStep",
          "presentationNextStep",
          "needIdentificationNextStep",
          "directionNextStep",
          "confirmationNextStep",
          "upsellNextStep",
          "availabilityNextStep",
          "dataCollectionNextStep",
          "closingNextStep",
          "customStepNames",
        ];

        knownDefaultFlowFields.forEach((field) => {
          if (defaultFlowData[field] !== undefined) {
            relevantFields[field] = defaultFlowData[field];
          }
        });

        // ✅ NOVO: Log para debug
        console.log("🔍 DEBUG - Campos mapeados para defaultFlow:", {
          originalKeys: Object.keys(defaultFlowData),
          mappedKeys: Object.keys(relevantFields),
          steps: formData.defaultFlowSteps,
          relevantFields: relevantFields,
        });

        formData.defaultFlow = {
          // Valores padrão
          greeting: "",
          presentation: "",
          needIdentification: "",
          direction: "",
          confirmation: "",
          upsell: "",
          availability: "",
          dataCollection: "",
          closing: "",
          greetingNextStep: "",
          presentationNextStep: "",
          needIdentificationNextStep: "",
          directionNextStep: "",
          confirmationNextStep: "",
          upsellNextStep: "",
          availabilityNextStep: "",
          dataCollectionNextStep: "",
          closingNextStep: "",
          customStepNames: {},

          // ✅ SOBRESCREVER com dados da API
          ...relevantFields,
        };
      }
    } else {
      // Valores padrão se não houver dados
      formData.defaultFlowSteps = [
        {
          id: "step_" + Date.now(),
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          placeholder:
            "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
          order: 0,
        },
      ];

      formData.defaultFlow = {
        greeting: "",
        presentation: "",
        needIdentification: "",
        direction: "",
        confirmation: "",
        upsell: "",
        availability: "",
        dataCollection: "",
        closing: "",
        greetingNextStep: "",
        presentationNextStep: "",
        needIdentificationNextStep: "",
        directionNextStep: "",
        confirmationNextStep: "",
        upsellNextStep: "",
        availabilityNextStep: "",
        dataCollectionNextStep: "",
        closingNextStep: "",
        customStepNames: {},
      };
    }

    // ===================================================================
    // VERIFICAÇÃO ADICIONAL: Log completo para debug
    // ===================================================================

    console.log("🔍 DEBUG - Resultado final do mapeamento defaultFlow:", {
      defaultFlowSteps: formData.defaultFlowSteps,
      defaultFlow: formData.defaultFlow,
      originalApiData: {
        hasDefaultFlow: !!(
          apiData.default_flow ||
          apiData.default_flow_steps ||
          apiData.defaultFlowSteps
        ),
        hasStepsInRoot: !!apiData.steps,
        rootKeys: Object.keys(apiData),
        stepsFromRoot: apiData.steps,
      },
    });

    // ===================================================================
    // TESTE: Verificar se os campos específicos foram mapeados
    // ===================================================================

    if (apiData.steps && Array.isArray(apiData.steps)) {
      console.log("🔍 TESTE - Verificando mapeamento específico dos steps:");

      apiData.steps.forEach((step, index) => {
        if (step.key) {
          const contentKey = step.key;
          const nextStepKey = `${step.key}NextStep`;

          console.log(`🔍 Step ${index} (${step.key}):`, {
            stepData: step,
            contentFromApi: apiData[contentKey],
            nextStepFromApi: apiData[nextStepKey],
            contentInFormData: formData.defaultFlow[contentKey],
            nextStepInFormData: formData.defaultFlow[nextStepKey],
            wasMapped: !!(
              formData.defaultFlow[contentKey] ||
              formData.defaultFlow[nextStepKey]
            ),
          });
        }
      });
    }

    // ===================================================================
    // FLUXOS ESPECIAIS - ✅ MANTIDO (já funcionando corretamente)
    // ===================================================================

    if (apiData.special_flows || apiData.specialFlows) {
      const specialFlowsData = apiData.special_flows || apiData.specialFlows;

      if (Array.isArray(specialFlowsData)) {
        formData.specialFlows = specialFlowsData.map((flow, flowIndex) => {
          console.log(`🔍 DEBUG - Mapeando fluxo especial ${flowIndex}:`, flow);

          // ✅ SIMPLES: Copiar tudo diretamente, sem conversões!
          const mappedFlow = {
            name: flow.name || "",
            triggers: flow.triggers || "",
            steps: flow.steps || [],

            // ✅ COPIA TODOS os outros campos diretamente
            // Não precisa converter nada porque já estão com step.key!
            ...Object.keys(flow)
              .filter((key) => !["name", "triggers", "steps"].includes(key))
              .reduce((acc, key) => {
                acc[key] = flow[key];
                return acc;
              }, {}),
          };

          console.log(`🔍 DEBUG - Fluxo especial ${flowIndex} mapeado:`, {
            original: flow,
            mapped: mappedFlow,
            copiedFields: Object.keys(flow).filter(
              (k) => !["name", "triggers", "steps"].includes(k)
            ),
          });

          return mappedFlow;
        });
      } else {
        formData.specialFlows = [];
      }
    } else {
      formData.specialFlows = [];
    }

    // ===================================================================
    // FUNÇÕES E SCHEMAS
    // ===================================================================

    // Funções habilitadas
    if (
      apiData.functions_enabled !== undefined ||
      apiData.functionsEnabled !== undefined
    ) {
      formData.functionsEnabled =
        apiData.functions_enabled || apiData.functionsEnabled || false;
    }

    // Schemas das funções
    if (
      apiData.function_responses ||
      apiData.functionResponses ||
      apiData.function_schemas ||
      apiData.functionSchemas
    ) {
      const functionsData =
        apiData.function_responses ||
        apiData.functionResponses ||
        apiData.function_schemas ||
        apiData.functionSchemas ||
        {};

      formData.functionSchemas = {};

      Object.keys(functionsData).forEach((functionId) => {
        const functionConfig = functionsData[functionId];

        if (functionConfig && typeof functionConfig === "object") {
          formData.functionSchemas[functionId] = {
            enabled: functionConfig.enabled || false,
            useDefault:
              functionConfig.useDefault !== undefined
                ? functionConfig.useDefault
                : true,
            customResponse: functionConfig.customResponse || "",
            registerClientData: functionConfig.registerClientData || false,
            registerContactData: functionConfig.registerContactData || false,
            fields: functionConfig.fields || functionConfig.params || {},
            customFields: functionConfig.customFields || [],
          };
        }
      });
    }

    // ===================================================================
    // MICRO-INTERAÇÕES
    // ===================================================================

    if (apiData.interactions) {
      formData.interactions = {
        conectores: {
          enabled: apiData.interactions.conectores?.enabled || false,
          value:
            apiData.interactions.conectores?.value ||
            MICRO_INTERACTIONS.conectores.defaultValue,
        },
        validacao: {
          enabled: apiData.interactions.validacao?.enabled || false,
          value:
            apiData.interactions.validacao?.value ||
            MICRO_INTERACTIONS.validacao.defaultValue,
        },
        celebracao: {
          enabled: apiData.interactions.celebracao?.enabled || false,
          value:
            apiData.interactions.celebracao?.value ||
            MICRO_INTERACTIONS.celebracao.defaultValue,
        },
        upsell: {
          enabled: apiData.interactions.upsell?.enabled || false,
          value:
            apiData.interactions.upsell?.value ||
            MICRO_INTERACTIONS.upsell.defaultValue,
        },
        emojis: {
          enabled: apiData.interactions.emojis?.enabled || false,
          value:
            apiData.interactions.emojis?.value ||
            MICRO_INTERACTIONS.emojis.defaultValue,
        },
        mantra: {
          enabled: apiData.interactions.mantra?.enabled || false,
          value:
            apiData.interactions.mantra?.value ||
            MICRO_INTERACTIONS.mantra.defaultValue,
        },
      };
    } else {
      // Valores padrão para interações
      formData.interactions = {
        conectores: {
          enabled: false,
          value: MICRO_INTERACTIONS.conectores.defaultValue,
        },
        validacao: {
          enabled: false,
          value: MICRO_INTERACTIONS.validacao.defaultValue,
        },
        celebracao: {
          enabled: false,
          value: MICRO_INTERACTIONS.celebracao.defaultValue,
        },
        upsell: {
          enabled: false,
          value: MICRO_INTERACTIONS.upsell.defaultValue,
        },
        emojis: {
          enabled: false,
          value: MICRO_INTERACTIONS.emojis.defaultValue,
        },
        mantra: {
          enabled: false,
          value: MICRO_INTERACTIONS.mantra.defaultValue,
        },
      };
    }

    console.log(
      "🔄 Dados mapeados da API (usando step.key com estrutura flat):",
      formData
    );
    console.log("🔍 Special flows mapeados:", formData.specialFlows);
    console.log("🔍 Default flow mapeado:", formData.defaultFlow);

    return formData;
  }, []);

  useEffect(() => {
    console.log(data);
    console.log(isEditing);
    if (isEditing && data) {
      console.log("Carregando dados do agente para edição:", data);
      setDataLoading(true);

      try {
        if (data.id) {
          setAgentId(data.id);
        }

        const mappedData = mapApiDataToForm(data);
        reset(mappedData);

        if (data.images && Array.isArray(data.images)) {
          setUploadedImages(data.images);
        }

        if (data.currentStep) {
          setCurrentStep(data.currentStep);
        }

        if (setNotify) {
          setNotify({
            type: "success",
            title: "Dados carregados",
            message: "Dados do agente carregados com sucesso para edição.",
            timer: 3000,
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados do agente:", error);
        if (setNotify) {
          setNotify({
            type: "error",
            title: "Erro ao carregar dados",
            message:
              "Não foi possível carregar os dados do agente para edição.",
          });
        }
      } finally {
        setDataLoading(false);
      }
    }
  }, [isEditing, data?.id]);

  // Reset form when switching from edit to create mode
  useEffect(() => {
    if (!isEditing && !data) {
      setAgentId(null);
      setUploadedImages([]);
      setCurrentStep(1);
      reset();
    }
  }, [isEditing, data, reset]);

  // Create image options for Select combinando API e prop (otimizado)
  const allImages = [...imageLibrary, ...apiImageLibrary, ...uploadedImages];
  const imageOptions = React.useMemo(
    () =>
      allImages.map((img) => ({
        value: `https://${img.url}`,
        label: img.name,
        image: `https://${img.url}`,
      })),
    [imageLibrary, apiImageLibrary, uploadedImages]
  );

  // Custom image select component (otimizado)
  const ImageSelect = React.memo(
    ({ value, onChange, placeholder, fieldName }) => {
      const formatOptionLabel = useCallback(
        ({ label, image }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={image}
              alt={label}
              style={{
                width: 30,
                height: 30,
                marginRight: 10,
                objectFit: "cover",
              }}
            />
            {label}
          </div>
        ),
        []
      );

      const handleChange = useCallback(
        (selected) => {
          if (selected && onChange) {
            const currentValue = value || "";
            const newValue =
              currentValue + (currentValue ? " " : "") + selected.value;
            onChange(newValue);
          }
        },
        [value, onChange]
      );

      return (
        <div className="mb-2">
          <Form.Label>
            <IconPhoto className="me-1" size={16} />
            Inserir imagem
          </Form.Label>
          <Select
            options={imageOptions}
            placeholder={placeholder}
            formatOptionLabel={formatOptionLabel}
            onChange={handleChange}
            isClearable
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: "35px",
                fontSize: "14px",
              }),
            }}
          />
        </div>
      );
    }
  );

  // Load image library from API
  const loadImageLibrary = useCallback(async () => {
    try {
      setImageLoading(true);
      const response = await listImages();
      setApiImageLibrary(response.data.results);
    } catch (error) {
      console.error("Erro ao carregar biblioteca de imagens:", error);
    } finally {
      setImageLoading(false);
    }
  }, []);

  // Load draft from API (only if not editing)
  const loadDraft = useCallback(async () => {
    if (isEditing) return;

    try {
      setDraftLoading(true);
      const response = await getCurrentDraft();

      if (response.data) {
        setSavedDraft(response.data);

        Swal.fire({
          title: "Rascunho encontrado!",
          text: "Deseja continuar de onde parou?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sim, continuar",
          cancelButtonText: "Começar do zero",
        }).then(async (result) => {
          if (result.isConfirmed) {
            // Carregar dados do rascunho
            Object.keys(response.data.data).forEach((key) => {
              setValue(key, response.data.data[key]);
            });
            setCurrentStep(response.data.currentStep || 1);
            if (response.data.images) {
              setUploadedImages(response.data.images);
            }
          } else {
            // Limpar rascunho do servidor
            try {
              await clearDraft(response.data.id);
              setSavedDraft(null);
            } catch (error) {
              console.error("Erro ao limpar rascunho:", error);
            }
          }
        });
      }
    } catch (error) {
      // Não há rascunho - comportamento normal
      console.log("Nenhum rascunho encontrado");
    } finally {
      setDraftLoading(false);
    }
  }, [setValue, isEditing]);

  // Load data on mount
  useEffect(() => {
    loadImageLibrary();
    loadDraft();
  }, [loadImageLibrary, loadDraft]);

  // Save draft function (disabled when editing)
  const handleSaveDraft = useCallback(async () => {
    if (isEditing) {
      if (setNotify) {
        setNotify({
          type: "warning",
          title: "Não disponível",
          message: "Salvar rascunho não está disponível no modo de edição.",
        });
      }
      return;
    }

    try {
      setDraftLoading(true);
      const formData = getValues();
      const draftData = prepareDraftData(currentStep, formData, uploadedImages);

      const response = await saveDraftAPI(draftData);
      setSavedDraft(response.data.draft);

      Swal.fire({
        icon: "success",
        title: "Rascunho salvo!",
        text: "Você pode continuar depois de onde parou.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Erro ao salvar rascunho:", error);
      Swal.fire({
        icon: "error",
        title: "Erro ao salvar rascunho",
        text: "Não foi possível salvar o rascunho. Tente novamente.",
      });
    } finally {
      setDraftLoading(false);
    }
  }, [currentStep, getValues, uploadedImages, isEditing, setNotify]);

  // Image upload handler
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    setImageLoading(true);

    try {
      if (files.length === 1) {
        // Upload único
        const file = files[0];

        // Verificar tamanho
        if (file.size > 1024 * 1024) {
          Swal.fire({
            icon: "error",
            title: "Arquivo muito grande",
            text: `A imagem ${file.name} excede o limite de 1MB.`,
          });
          setImageLoading(false);
          return;
        }

        const formData = createImageFormData(file);
        const response = await uploadImage(formData);

        setUploadedImages((prev) => [...prev, response.data]);

        Swal.fire({
          icon: "success",
          title: "Imagem enviada!",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        // Upload múltiplo
        const validFiles = files.filter((file) => {
          if (file.size > 1024 * 1024) {
            Swal.fire({
              icon: "warning",
              title: "Arquivo ignorado",
              text: `A imagem ${file.name} excede o limite de 1MB e foi ignorada.`,
              timer: 3000,
            });
            return false;
          }
          return true;
        });

        if (validFiles.length === 0) {
          setImageLoading(false);
          return;
        }

        const formData = createBulkImageFormData(validFiles);
        const response = await bulkUploadImages(formData);

        const { uploaded, errors, total_uploaded, total_errors } =
          response.data;

        if (uploaded) {
          setUploadedImages((prev) => [...prev, ...uploaded]);
        }

        if (total_errors > 0) {
          Swal.fire({
            icon: "warning",
            title: "Upload parcialmente concluído",
            text: `${total_uploaded} imagens enviadas com sucesso, ${total_errors} com erro.`,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: `${total_uploaded} imagens enviadas!`,
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }
    } catch (error) {
      console.error("Erro no upload:", error);
      Swal.fire({
        icon: "error",
        title: "Erro no upload",
        text: "Erro ao enviar as imagens. Tente novamente.",
      });
    } finally {
      setImageLoading(false);
    }
  };

  // Remove image
  const removeImage = async (imageId) => {
    // Se é uma imagem carregada da API, deletar do servidor
    if (apiImageLibrary.some((img) => img.id === imageId)) {
      try {
        await deleteImage(imageId);
        setApiImageLibrary((prev) => prev.filter((img) => img.id !== imageId));

        Swal.fire({
          icon: "success",
          title: "Imagem removida!",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível remover a imagem.",
        });
      }
    } else {
      // Se é uma imagem do upload atual, apenas remover do estado local
      setUploadedImages((prev) => prev.filter((img) => img.id !== imageId));
    }
  };

  // AI Fill handler
  const handleAIFill = (stepKey, stepInfo) => {
    const currentData = getValues();
    setAiStepInfo({
      ...stepInfo,
      stepKey,
      agentName: currentData.agentName,
      businessArea: AREA_ACTIVITY_OPTIONS.find(
        (opt) => opt.value === currentData.agentArea
      )?.label,
      personality: currentData.agentPersona?.join(", "),
    });
    setShowAIModal(true);
  };

  const handleAIGenerate = async (prompt) => {
    setAiLoading(true);
    try {
      // Aqui você faria a chamada para sua API de IA
      // Por enquanto, vou simular uma resposta
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simula delay da API

      const mockResponse =
        "Oi! Eu sou a " +
        (aiStepInfo?.agentName || "assistente") +
        "! 😊 Como posso te ajudar hoje?";

      // Atualizar o campo correspondente
      setValue(aiStepInfo.stepKey, mockResponse);

      setShowAIModal(false);
      setAiStepInfo(null);

      Swal.fire({
        icon: "success",
        title: "Conteúdo gerado com IA!",
        text: "O texto foi gerado e inserido na etapa.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao gerar conteúdo",
        text: "Não foi possível gerar o conteúdo com IA. Tente novamente.",
      });
    } finally {
      setAiLoading(false);
    }
  };

  // Apply template
  const applyTemplate = (templateKey) => {
    const template = CONVERSATION_TEMPLATES[templateKey];
    if (!template) return;

    Swal.fire({
      title: `Aplicar ${template.name}?`,
      text: "Isso substituirá o conteúdo atual do fluxo padrão.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, aplicar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        // Aplicar template no fluxo padrão
        const newDefaultFlow = { ...getValues("defaultFlow") };

        template.steps.forEach((step) => {
          newDefaultFlow[step.key] = "";
          if (!newDefaultFlow.customStepNames) {
            newDefaultFlow.customStepNames = {};
          }
          newDefaultFlow.customStepNames[step.key] = step.label;
        });

        setValue("defaultFlow", newDefaultFlow);

        Swal.fire({
          icon: "success",
          title: "Template aplicado!",
          text: "O template foi aplicado ao fluxo padrão.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    });
  };

  // Navigation functions
  const nextStep = async () => {
    const currentSchema = schemas[`step${currentStep}`];
    if (currentSchema) {
      const currentData = getValues();
      try {
        await currentSchema.validate(currentData, { abortEarly: false });
        setCurrentStep((prev) => Math.min(prev + 1, 7));
      } catch (validationError) {
        Swal.fire({
          icon: "error",
          title: "Campos obrigatórios",
          text: "Por favor, preencha todos os campos obrigatórios antes de continuar.",
        });
      }
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 7));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Add special flow (copying default flow structure)
  const addSpecialFlow = useCallback(() => {
    const currentFlows = watch("specialFlows") || [];

    const newFlow = {
      name: "",
      triggers: "",
      steps: [
        {
          id: "special_step_" + Date.now() + "_" + currentFlows.length,
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          order: 0,
        },
      ],
      // ✅ CORREÇÃO: Remover campos que serão gerenciados pelo formulário
    };

    setValue("specialFlows", [...currentFlows, newFlow], {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [setValue]);

  // Remove special flow
  const removeSpecialFlow = useCallback(
    (index) => {
      Swal.fire({
        title: "Excluir fluxo?",
        text: "Esta ação não poderá ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      }).then((result) => {
        if (result.isConfirmed) {
          const currentFlows = watch("specialFlows") || [];
          const newFlows = currentFlows.filter((_, i) => i !== index);
          setValue("specialFlows", newFlows, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      });
    },
    [setValue]
  );

  const memoizedSpecialFlows = React.useMemo(() => {
    // Verificar se há mudanças reais nos dados
    const currentSpecialFlowsStr = JSON.stringify(specialFlows);

    if (prevSpecialFlowsRef.current === currentSpecialFlowsStr) {
      return specialFlowsRef.current;
    }

    prevSpecialFlowsRef.current = currentSpecialFlowsStr;

    if (!Array.isArray(specialFlows)) {
      specialFlowsRef.current = [];
      return [];
    }

    // ✅ CORREÇÃO: Processar sem getValues() que causa instabilidade
    const processedFlows = specialFlows.map((flow, index) => ({
      ...flow,
      index,
      // ✅ CORREÇÃO: Remover formData que causava problemas
      // A estrutura será acessada diretamente via watch() quando necessário
    }));

    specialFlowsRef.current = processedFlows;
    return processedFlows;
  }, [specialFlows]);

  // Move flow for drag and drop
  const moveFlow = useCallback(
    (dragIndex, hoverIndex) => {
      // Obter dados atuais diretamente
      const currentFlows = watch("specialFlows") || [];
      const newFlows = [...currentFlows];
      const draggedFlow = newFlows[dragIndex];

      newFlows.splice(dragIndex, 1);
      newFlows.splice(hoverIndex, 0, draggedFlow);

      setValue("specialFlows", newFlows, {
        shouldValidate: false,
        shouldDirty: false,
      });
    },
    [setValue] // ✅ CORREÇÃO: Apenas setValue como dependência estável
  );

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      console.log("🔍 Dados do formulário no onSubmit:", data);

      const defaultFlowWithContent = {
        steps: data.defaultFlowSteps || [], // Metadados das etapas
        // ✅ CORREÇÃO: Coletar conteúdo das etapas do fluxo padrão
        ...Object.keys(data.defaultFlow || {}).reduce((acc, key) => {
          if (data.defaultFlow[key]) {
            acc[key] = data.defaultFlow[key];
          }
          return acc;
        }, {}),
      };

      console.log(
        "🔍 defaultFlowWithContent preparado:",
        defaultFlowWithContent
      );

      // ✅ CORREÇÃO CRÍTICA: Preparar specialFlows com acesso correto aos dados
      const specialFlowsWithContent = [];

      if (data.specialFlows && Array.isArray(data.specialFlows)) {
        data.specialFlows.forEach((flow, flowIndex) => {
          console.log(`🔍 Processando fluxo especial ${flowIndex}:`, flow);

          const flowName = flow.name || "";
          const flowTriggers = flow.triggers || "";
          const flowSteps = flow.steps || [];

          // ✅ CORREÇÃO: Coletar conteúdo das etapas usando acesso DIRETO ao flow
          const flowContentData = {};

          if (Array.isArray(flowSteps)) {
            flowSteps.forEach((step) => {
              if (step && step.key) {
                // ✅ CORREÇÃO CRÍTICA: Acessar dados diretamente do flow, não de uma estrutura aninhada
                const stepContent = flow[step.key];
                const nextStepContent = flow[`${step.key}NextStep`];

                console.log(`🔍 Coletando conteúdo da etapa ${step.key}:`, {
                  stepId: step.id,
                  stepKey: step.key,
                  content: stepContent,
                  nextStep: nextStepContent,
                  hasContent: stepContent && stepContent.trim() !== "",
                });

                if (stepContent && stepContent.trim() !== "") {
                  // ✅ CORREÇÃO: Usar step.key como chave para o conteúdo
                  flowContentData[step.key] = stepContent.trim();
                }

                // ✅ CORREÇÃO: Coletar nextStep se existir
                if (nextStepContent && nextStepContent.trim() !== "") {
                  flowContentData[`${step.key}NextStep`] =
                    nextStepContent.trim();
                }
              }
            });
          }

          // ✅ CORREÇÃO: Só adicionar fluxo se tiver nome E conteúdo
          const hasContent = Object.keys(flowContentData).length > 0;
          const hasValidName = flowName && flowName.trim() !== "";

          if (hasValidName && hasContent) {
            const specialFlowData = {
              name: flowName.trim(),
              triggers: flowTriggers.trim(),
              steps: flowSteps, // Metadados das etapas
              ...flowContentData, // Conteúdo das etapas
            };

            console.log(
              `🔍 ✅ Fluxo especial ${flowIndex} adicionado:`,
              specialFlowData
            );
            specialFlowsWithContent.push(specialFlowData);
          } else {
            console.log(
              `🔍 ❌ Fluxo especial ${flowIndex} ignorado - Nome válido: ${hasValidName}, Tem conteúdo: ${hasContent}`
            );
            console.log(`🔍 Debug flow ${flowIndex}:`, {
              flowName,
              flowTriggers,
              flowSteps,
              flowContentData,
              availableFlowProps: Object.keys(flow).filter(
                (k) => typeof flow[k] === "string" && flow[k].trim() !== ""
              ),
            });
          }
        });
      }

      // ✅ CORREÇÃO: Preparar dados do agente com estrutura otimizada
      const agentData = prepareAgentData(
        {
          // Mapear campos do formulário para o formato da API
          name: data.agentName,
          description: `${data.agentJob} ${data.agentPersona?.join(
            ", "
          )} para ${
            AREA_ACTIVITY_OPTIONS.find((opt) => opt.value === data.agentArea)
              ?.label || data.agentArea
          }`,
          agent_name: data.agentName,
          agent_job: data.agentJob,
          agent_gender: data.agentGender,
          agent_persona: data.agentPersona,
          agent_area: data.agentArea,
          language: data.language,
          language_style: data.languageStyle,
          fundamental_rules: data.fundamentalRules,
          prohibited_words: data.prohibitedWords,
          preferred_words: data.preferredWords,
          business_info: data.businessInfo,
          address: data.address,
          payment_methods: data.paymentMethods,

          // ✅ CORREÇÃO: Estrutura corrigida para fluxos
          default_flow: defaultFlowWithContent,
          special_flows: specialFlowsWithContent,

          functions_enabled: data.functionsEnabled,
          function_responses: data.functionSchemas,
          function_schemas: getGeneratedSchemas || {},
          interactions: data.interactions,
          // generated_prompt: generatePromptPreview(),
          account: user.account_id,
        },
        uploadedImages
      );

      console.log("🔍 Dados finais para envio:", agentData);

      let response;

      if (isEditing && agentId) {
        // Atualizar agente existente
        console.log("🔄 Atualizando agente:", agentId);
        response = await updateAgent(agentId, agentData);

        Swal.fire({
          icon: "success",
          title: "Agente atualizado com sucesso!",
          text: "As alterações foram salvas.",
          timer: 3000,
        });
      } else {
        // Criar novo agente
        console.log("🆕 Criando novo agente");
        agentData["is_active"] = 0;
        response = await createAgent(agentData);

        Swal.fire({
          icon: "success",
          title: "Agente criado com sucesso!",
          text: "Seu agente de IA foi configurado e está pronto para uso.",
          timer: 3000,
        });
      }

      // Limpar rascunho após sucesso (apenas quando criando)
      if (!isEditing && savedDraft?.id) {
        try {
          await clearDraft(savedDraft.id);
          setSavedDraft(null);
        } catch (error) {
          console.error("Erro ao limpar rascunho:", error);
        }
      }

      setNotify({
        type: "success",
        title: "success",
        message: isEditing
          ? "Agent editado com sucesso!"
          : "Agente criado com sucesso!",
      });

      setTimeout(() => {
        window.location.href = "/agents";
      }, 2500);

      // Callback de sucesso
      // if (onSave) {
      //   await onSave(response.data);
      // }

      // if (onClose) {
      //   onClose();
      // }
    } catch (error) {
      console.error(
        `Erro ao ${isEditing ? "atualizar" : "criar"} agente:`,
        error
      );

      // Tratamento de erro mais específico
      let errorMessage = `Ocorreu um erro ao ${
        isEditing ? "atualizar" : "salvar"
      } o agente. Tente novamente.`;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: "error",
        title: `Erro ao ${isEditing ? "atualizar" : "salvar"}`,
        text: errorMessage,
      });

      // Notificar via setNotify se disponível
      if (setNotify) {
        setNotify({
          type: "error",
          title: `Erro ao ${isEditing ? "atualizar" : "salvar"}`,
          message: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Step titles and icons (atualizado para 7 steps)
  const steps = [
    { title: "Identidade", icon: IconUser },
    { title: "Linguagem", icon: IconPalette },
    { title: "Sobre o Negócio", icon: IconBuilding },
    { title: "Como Conversar", icon: IconMessageCircle },
    { title: "Funções", icon: IconFunction },
    { title: "Interações", icon: IconHeart },
    { title: "Prompt Final", icon: IconFileText },
  ];

  // Flow step options updated with better labels for basic mode (otimizado)
  const getFlowStepsForMode = React.useMemo(
    () => (mode) => {
      if (mode === "basic") {
        return [
          {
            key: "greeting",
            label: "Cumprimentar o Cliente",
            icon: "👋",
            description:
              "Como o agente vai cumprimentar cada pessoa que chegar",
          },
          {
            key: "needIdentification",
            label: "Descobrir o que a Pessoa Quer",
            icon: "🤔",
            description: "Como perguntar o que o cliente está procurando",
          },
          {
            key: "presentation",
            label: "Explicar Como Você Pode Ajudar",
            icon: "💡",
            description: "Como apresentar seus produtos ou serviços",
          },
          {
            key: "closing",
            label: "Finalizar o Atendimento",
            icon: "🎯",
            description: "Como encerrar a conversa e definir próximos passos",
          },
        ];
      } else {
        return [
          {
            key: "greeting",
            label: "Saudação",
            icon: "🎯",
            description: "Primeira mensagem do agente",
          },
          {
            key: "presentation",
            label: "Apresentação",
            icon: "🏢",
            description: "Como o agente apresenta o seu negócio",
          },
          {
            key: "needIdentification",
            label: "Identificação da Necessidade",
            icon: "🔍",
            description: "Como descobrir o que o cliente precisa",
          },
          {
            key: "direction",
            label: "Direcionamento",
            icon: "🎯",
            description: "Como direcionar o cliente para a solução",
          },
          {
            key: "confirmation",
            label: "Confirmação",
            icon: "✅",
            description: "Como confirmar detalhes e informações",
          },
          {
            key: "upsell",
            label: "Upsell (Opcional)",
            icon: "💡",
            description: "Como oferecer produtos ou serviços adicionais",
          },
          {
            key: "availability",
            label: "Disponibilidade",
            icon: "📅",
            description: "Como verificar e apresentar disponibilidade",
          },
          {
            key: "dataCollection",
            label: "Coleta de Dados",
            icon: "📋",
            description: "Como coletar informações necessárias",
          },
          {
            key: "closing",
            label: "Finalização",
            icon: "🎉",
            description: "Como finalizar o atendimento",
          },
        ];
      }
    },
    []
  );

  // Função para renderizar o conteúdo da etapa atual
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepIdentity
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            control={control}
          />
        );
      case 2:
        return (
          <StepLanguage
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      case 3:
        return (
          <StepBusiness
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            control={control}
          />
        );
      case 4:
        return (
          <StepConversation
            register={register}
            watch={watch}
            setValue={setValue}
            flowMode={flowMode}
            setFlowMode={setFlowMode}
            handleAIFill={handleAIFill}
            ImageSelect={ImageSelect}
            CONVERSATION_TEMPLATES={CONVERSATION_TEMPLATES}
            applyTemplate={applyTemplate}
            NEXT_STEP_OPIONS={NEXT_STEP_OPIONS}
            addSpecialFlow={addSpecialFlow}
            removeSpecialFlow={removeSpecialFlow}
            moveFlow={moveFlow}
            specialFlows={memoizedSpecialFlows}
            getFlowStepsForMode={getFlowStepsForMode}
          />
        );
      case 5:
        return (
          <StepFunctions
            register={register}
            watch={watch}
            setValue={setValue}
            handleAIFill={handleAIFill}
            onGetSchemas={setGetGeneratedSchemas}
          />
        );
      case 6:
        return (
          <StepInteractions
            register={register}
            watch={watch}
            setValue={setValue}
            handleAIFill={handleAIFill}
            MICRO_INTERACTIONS={MICRO_INTERACTIONS}
          />
        );
      case 7:
        return (
          <StepFinalPrompt
            generatePromptPreview={null}
            copyPrompt={null}
            exportPrompt={null}
            getValues={getValues}
            AREA_ACTIVITY_OPTIONS={AREA_ACTIVITY_OPTIONS}
            uploadedImages={uploadedImages}
            isEditing={isEditing}
          />
        );
      default:
        return null;
    }
  };

  // Loading state during data loading
  if (dataLoading) {
    return (
      <Container className="main-container">
        <div className="text-center py-5">
          <Spinner className="mb-3" />
          <h5>Carregando dados do agente...</h5>
          <p className="text-muted">
            Aguarde enquanto carregamos os dados para edição.
          </p>
        </div>
      </Container>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="main-container">
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Row>
            <Col lg={11} sm={12} className="mx-auto mt-3">
              {/* Navigation Tabs */}
              <Nav variant="tabs" className="mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="wizard">
                    <IconSettings className="me-2" size={16} />
                    {isEditing ? "Editar Agente" : "Criador de Agentes"}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="images">
                    <IconPhoto className="me-2" size={16} />
                    Biblioteca de Imagens
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                {/* Wizard Tab */}
                <Tab.Pane eventKey="wizard">
                  <Row>
                    <Col lg={12}>
                      {/* Header */}
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h3 className="mb-0">
                          <IconRocket className="me-2" />
                          {isEditing
                            ? "Editar Agente IA"
                            : "Criador de Agentes IA"}
                          {isEditing && (
                            <Badge bg="warning" className="ms-2">
                              Modo Edição
                            </Badge>
                          )}
                        </h3>
                        <div>
                          {/* <Button
                              variant="outline-secondary"
                              size="sm"
                              className="me-2"
                              onClick={() => setShowPreview(!showPreview)}
                            >
                              <IconEye className="me-1" size={16} />
                              {showPreview ? "Ocultar" : "Preview"}
                            </Button> */}
                          {!isEditing && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleSaveDraft}
                              disabled={draftLoading}
                            >
                              {draftLoading ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <IconDeviceFloppy className="me-1" size={16} />
                              )}
                              Salvar Rascunho
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Alert for edit mode */}
                      {isEditing && (
                        <Alert variant="info" className="mb-4 p-2">
                          <strong>Modo de Edição:</strong> Você está editando um
                          agente existente. Todas as alterações serão salvas ao
                          finalizar.
                        </Alert>
                      )}

                      {/* Progress Bar */}
                      <ProgressBar
                        now={(currentStep / 7) * 100}
                        className="mb-4"
                        style={{ height: "8px" }}
                      />

                      {/* Steps Navigation */}
                      <div
                        className="d-flex justify-content-center mb-4"
                        style={{ flexWrap: "wrap", gap: "10px" }}
                      >
                        {steps.map((step, index) => {
                          const StepIcon = step.icon;
                          const stepNumber = index + 1;
                          const isActive = currentStep === stepNumber;
                          const isCompleted = currentStep > stepNumber;

                          return (
                            <div
                              key={stepNumber}
                              className={`d-flex flex-column align-items-center ${
                                isActive
                                  ? "text-primary"
                                  : isCompleted
                                  ? "text-success"
                                  : "text-muted"
                              }`}
                              style={{ cursor: "pointer", minWidth: "80px" }}
                              onClick={() =>
                                stepNumber < currentStep &&
                                setCurrentStep(stepNumber)
                              }
                            >
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                                  isActive
                                    ? "bg-primary text-white"
                                    : isCompleted
                                    ? "bg-success text-white"
                                    : "bg-light text-muted"
                                }`}
                                style={{ width: "40px", height: "40px" }}
                              >
                                <StepIcon size={20} />
                              </div>
                              <small className="text-center">
                                {step.title}
                              </small>
                            </div>
                          );
                        })}
                      </div>

                      {/* Form Content */}
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <Card className="shadow-sm">
                          <Card.Body>
                            {/* Renderizar conteúdo da etapa atual */}
                            {renderStepContent()}
                          </Card.Body>

                          {/* Navigation Footer */}
                          <Card.Footer className="d-flex justify-content-between align-items-center">
                            <Button
                              variant="outline-secondary"
                              onClick={prevStep}
                              disabled={currentStep === 1}
                            >
                              <IconChevronLeft className="me-1" size={16} />
                              Anterior
                            </Button>

                            <span className="text-muted">
                              Etapa {currentStep} de 7
                            </span>

                            {currentStep < 7 ? (
                              <Button variant="primary" onClick={nextStep}>
                                Próximo
                                <IconChevronRight className="ms-1" size={16} />
                              </Button>
                            ) : (
                              <Button
                                variant="success"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? (
                                  <Spinner size="sm" className="me-1" />
                                ) : (
                                  <IconDeviceFloppy
                                    className="me-1"
                                    size={16}
                                  />
                                )}
                                {isEditing
                                  ? "Salvar Alterações"
                                  : "Criar Agente"}
                              </Button>
                            )}
                          </Card.Footer>
                        </Card>
                      </Form>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Images Library Tab */}
                <Tab.Pane eventKey="images">
                  <ImageLibraryTab
                    imageLibrary={imageLibrary}
                    apiImageLibrary={apiImageLibrary}
                    uploadedImages={uploadedImages}
                    imageLoading={imageLoading}
                    handleImageUpload={handleImageUpload}
                    removeImage={removeImage}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>

        {/* AI Fill Modal */}
        <AIFillModal
          show={showAIModal}
          onHide={() => {
            setShowAIModal(false);
            setAiStepInfo(null);
          }}
          onGenerate={handleAIGenerate}
          stepInfo={aiStepInfo}
          loading={aiLoading}
        />
      </div>
    </DndProvider>
  );
});

AgentWizard.displayName = "AgentWizard";

export default AgentWizard;
